// TODO [tech-debt]: nop-access no longer uses this component (has its own at apps/nop-access/.../EtaSections.tsx).
// Clean up nop-access-specific code (e.g. displayInUserLocalTime, enablePendingLabels, TruncatedWithTooltip/IconButton for edit).
// Consider moving this into nop-connect and removing from shared lib.
import { ConditionalRender } from '@ui';
import { useAppContext } from '@features/contexts/AppProvider';
import React, { ElementType, useCallback, useMemo } from 'react';
import cn from 'classnames';
import { Chip, IconButton, TruncatedWithTooltip } from '@ds';
import { MdOutlineModeEditOutline } from 'react-icons/md';
import {
  convertToUserLocalTime,
  formatDateTimeString,
  formatDateTimeStringToTimeZone,
  getSelectedDeviceFromTracking,
  getTxCenterEtaDateTime,
} from '@/utils/helpers';
import {
  CaseFacility,
  ClinicalDataItem,
  DonorORData,
  ETADelayData,
  TxCenterORData,
  TxEtaHospitalData,
} from '@/api/apis/case';
import { RouteDeviceTracking } from '@/api/apis/route';
import { getTimeDelayString } from './utils';
import { Placeholder } from './Placeholder';
import { PLACEHOLDER_DATE_TIME_FORMAT } from '@/constants/date.constants';
import { EtaContent, EtaType } from '@/types';

type DateFieldDataProps = {
  onEditOpen?: () => void;
};

type ETASectionsProps = {
  displayInUserLocalTime?: boolean;
  enablePendingLabels?: boolean;
  displayEtaTypes: EtaType[];
  donorORData?: DonorORData & ETADelayData;
  txEtaData?: TxEtaHospitalData &
    ETADelayData & {
      txCenterDetails: CaseFacility;
    };
  txCenterOREta?: TxCenterORData & ETADelayData & DateFieldDataProps;
  deviceTracking?: RouteDeviceTracking;
  formatedDelay?: string;
  delayInfo?: ClinicalDataItem;
  SectionRowComponent: ElementType;
};

const getLabel = (etaContent: EtaContent[], etaType: EtaType) =>
  etaContent.find((eta: EtaContent) => eta.type === etaType)?.label ?? '';

export const EtaSections = React.memo(
  ({
    displayInUserLocalTime,
    enablePendingLabels,
    displayEtaTypes,
    donorORData,
    txEtaData,
    txCenterOREta,
    deviceTracking,
    formatedDelay,
    SectionRowComponent,
    delayInfo,
  }: ETASectionsProps) => {
    const {
      caseDetails: { etaContent },
    } = useAppContext();

    const dateTimeFormatFn = useMemo(
      () => (displayInUserLocalTime ? convertToUserLocalTime : formatDateTimeString),
      [displayInUserLocalTime],
    );

    type RenderDateTimeParams = {
      etaType: EtaType;
      dateTimeString: string | null;
      delayTimeString?: string | null;
      comment?: string | null;
      onEditOpen?: () => void;
    };

    const renderDateTimeStringContent = useCallback(
      ({ etaType, dateTimeString, delayTimeString, comment, onEditOpen }: RenderDateTimeParams) => {
        const label = getLabel(etaContent, etaType);
        const isPendingPlaceholder = dateTimeString === PLACEHOLDER_DATE_TIME_FORMAT;
        const shouldShowPending = enablePendingLabels && isPendingPlaceholder;

        return (
          <SectionRowComponent
            key={etaType}
            label={label}
            classNames={{ value: 'truncate text-m-m px-3 w-full flex justify-between' }}
          >
            <ConditionalRender show={!!dateTimeString} placeholder={<Placeholder />}>
              <div className="flex flex-col gap-1 min-w-0">
                <div className="flex flex-col gap-2">
                  <span>{shouldShowPending ? 'Pending' : dateTimeString}</span>
                  {delayTimeString && (
                    <Chip className="h-5 text-danger-30 bg-danger-70">
                      <span className="text-s-sb">{delayTimeString}</span>
                    </Chip>
                  )}
                </div>
                <ConditionalRender show={Boolean(comment)}>
                  <TruncatedWithTooltip
                    tooltipOptions={{ placement: 'top', showArrow: true, className: 'max-w-100' }}
                    value={`${comment}`}
                    valueClassName="text-xs-r! text-base-5 pb-1"
                  />
                </ConditionalRender>
              </div>
            </ConditionalRender>
            <ConditionalRender show={!!onEditOpen}>
              <IconButton
                size="sm"
                variant="light"
                aria-label="Edit TX Center OR"
                startContent={<MdOutlineModeEditOutline />}
                onClick={onEditOpen}
              />
            </ConditionalRender>
          </SectionRowComponent>
        );
      },
      [SectionRowComponent, enablePendingLabels, etaContent],
    );

    return (
      <>
        {displayEtaTypes.map((etaType) => {
          let dateTimeString: string | null = null;
          switch (etaType) {
            case EtaType.donorOR:
              dateTimeString = donorORData
                ? dateTimeFormatFn(
                    donorORData.donorHospitalDate,
                    donorORData.donorHospitalTime,
                    donorORData.donorHospitalTimeZone,
                  )
                : null;
              return renderDateTimeStringContent({
                etaType,
                dateTimeString,
                delayTimeString: donorORData?.displayDelay
                  ? getTimeDelayString(dateTimeString, donorORData.etaDelayEnd)
                  : null,
              });

            // txCenterSta: STA2 (device scheduled) > STA1 (case data in UTC)
            case EtaType.txCenterSta: {
              const selectedDevice = getSelectedDeviceFromTracking(deviceTracking);
              const txCenterTimezone = txEtaData?.txCenterDetails?.location.timezone;

              if (selectedDevice?.scheduledArrivalTime) {
                // STA2: TMDX Drive scheduled arrival
                dateTimeString = displayInUserLocalTime
                  ? convertToUserLocalTime(selectedDevice.scheduledArrivalTime, undefined, undefined)
                  : formatDateTimeStringToTimeZone(selectedDevice.scheduledArrivalTime, txCenterTimezone);
              } else if (displayInUserLocalTime) {
                dateTimeString = convertToUserLocalTime(
                  txEtaData?.txEtaHospitalDate,
                  txEtaData?.txEtaHospitalTime,
                  'UTC',
                );
              } else {
                // STA1: Logistics builder / case data
                dateTimeString = getTxCenterEtaDateTime(
                  txEtaData?.txEtaHospitalDate,
                  txEtaData?.txEtaHospitalTime,
                  txCenterTimezone,
                );
              }

              return renderDateTimeStringContent({
                etaType,
                dateTimeString,
                delayTimeString: txEtaData?.displayDelay
                  ? getTimeDelayString(dateTimeString, txEtaData.etaDelayEnd)
                  : null,
              });
            }

            // txCenterEta: Real-time ETA from device tracking
            case EtaType.txCenterEta: {
              const selectedDevice = getSelectedDeviceFromTracking(deviceTracking);
              const txCenterTimezone = txEtaData?.txCenterDetails?.location.timezone;

              if (selectedDevice?.estimatedArrivalTime) {
                dateTimeString = displayInUserLocalTime
                  ? convertToUserLocalTime(selectedDevice.estimatedArrivalTime, undefined, undefined)
                  : formatDateTimeStringToTimeZone(selectedDevice.estimatedArrivalTime, txCenterTimezone);
              }

              return renderDateTimeStringContent({
                etaType,
                dateTimeString,
              });
            }
            case EtaType.txCenterOr:
              dateTimeString = txCenterOREta
                ? dateTimeFormatFn(
                    txCenterOREta.recipientHospitalDate,
                    txCenterOREta.recipientHospitalTime,
                    txCenterOREta.recipientHospitalTimeZone,
                  )
                : null;
              return renderDateTimeStringContent({
                etaType,
                dateTimeString,
                delayTimeString: txCenterOREta?.displayDelay
                  ? getTimeDelayString(dateTimeString, txCenterOREta.etaDelayEnd)
                  : null,
                comment: txCenterOREta?.recipientHospitalORChangeReason,
                onEditOpen: txCenterOREta?.onEditOpen,
              });
            case EtaType.delay: {
              return (
                <SectionRowComponent key={etaType} label={getLabel(etaContent, etaType)}>
                  <ConditionalRender show={!!delayInfo} placeholder={<Placeholder />}>
                    <>
                      <div className={cn({ 'text-danger-30': !!delayInfo?.timestamp })}>{formatedDelay}</div>
                      <div title={delayInfo?.details} className="text-xs-r truncate w-52">
                        {delayInfo?.details}
                      </div>
                    </>
                  </ConditionalRender>
                </SectionRowComponent>
              );
            }
            default:
              return null;
          }
        })}
      </>
    );
  },
);
